import { zodResolver } from '@hookform/resolvers/zod';
import { useBlocker } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { createContext, useContext, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import {
  FormProvider,
  useForm,
  useFormContext,
  type DefaultValues,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from 'react-hook-form';
import { toast } from '@/lib/toast';
import type { ZodType } from 'zod';
import { ApiError } from '@/lib/api';
import { reportProblems, scrollToFirstProblem } from '@/lib/feedback';
import { cn, newIdempotencyKey } from '@/lib/utils';
import { Button, type ButtonProps } from '../ui/button';
import { Alert } from '../ui/misc';

/**
 * Standard form hook. Validation pattern used everywhere:
 *  - a field is validated when the user leaves it (onTouched), then re-validated on every keystroke;
 *  - the same Zod schema runs on the server, so messages are identical;
 *  - server field errors are placed under the matching field;
 *  - submit is single-flight and carries an idempotency key.
 */
export function useAppForm<TIn extends FieldValues, TOut = TIn>(schema: ZodType<TOut, TIn>, defaultValues: DefaultValues<TIn>) {
  return useForm<TIn, unknown, TOut>({
    resolver: zodResolver(schema as never) as never,
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: true,
  });
}

interface AppFormProps<TIn extends FieldValues, TOut> {
  form: UseFormReturn<TIn, unknown, TOut>;
  onSubmit(values: TOut, ctx: { idempotencyKey: string }): Promise<unknown>;
  children: ReactNode;
  className?: string;
  /** Message toast on success; omit to stay silent. */
  successMessage?: string;
  /** Warn before navigating away with unsaved changes (default true). */
  guardUnsaved?: boolean;
  /** Enter on the last field submits (short forms such as sign-in); elsewhere Enter only moves to the next field. */
  submitOnLastEnter?: boolean;
  /** Heading of the error box shown when the request fails. */
  errorTitle?: string;
}

export function AppForm<TIn extends FieldValues, TOut>({ form, onSubmit, children, className, successMessage, guardUnsaved = true, errorTitle = 'Could not save', submitOnLastEnter = false }: AppFormProps<TIn, TOut>) {
  const [formError, setFormError] = useState<string | null>(null);
  const keyRef = useRef(newIdempotencyKey());
  const submittingRef = useRef(false);
  const { isDirty, isSubmitting, isSubmitSuccessful } = form.formState;
  const formRef = useRef<HTMLFormElement>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const labels = useRef(new Map<string, string>());

  useBlocker({
    shouldBlockFn: () => !window.confirm('You have unsaved changes. Leave this page and discard them?'),
    disabled: !guardUnsaved || !isDirty || isSubmitting || isSubmitSuccessful,
    enableBeforeUnload: () => guardUnsaved && isDirty && !isSubmitSuccessful,
  });

  const handle = form.handleSubmit(
    async (values) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      setFormError(null);
      try {
        saveOutcomeTold.current = false;
        await onSubmit(values, { idempotencyKey: keyRef.current });
        keyRef.current = newIdempotencyKey();
        // One message per click: when the save went on to submit, that message ("Submitted for approval…") says it all.
        if (successMessage && !saveOutcomeTold.current) toast.success(successMessage);
      } catch (e) {
        applyServerErrors(form, e, setFormError);
        toast.error(errorTitle, { id: 'form-problems', description: e instanceof ApiError ? e.message : 'Something went wrong. Please try again.' });
        scrollToFirstProblem(formRef.current, alertRef.current);
      } finally {
        submittingRef.current = false;
      }
    },
    (errors) => {
      setFormError(null);
      // Name every field that stops the save, by its label, and go to the first one.
      const problems = flattenErrors(errors).map(([path, msg]) => {
        const label = labelFor(labels.current, path);
        return msg.toLowerCase().includes(label.toLowerCase()) ? msg : `${label}: ${msg}`;
      });
      reportProblems(problems.length ? problems : ['Some entries are not valid.'], { root: formRef.current });
    },
  );

  return (
    <FormProvider {...form}>
      <LabelsContext.Provider value={labels.current}>
      <form
        ref={formRef}
        noValidate
        className={className}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void handle(e);
        }}
        onKeyDown={(e) => enterToNext(e, submitOnLastEnter)}
      >
        {formError && (
          <div ref={alertRef} data-invalid="true" className="mb-4 scroll-mt-24">
            <Alert title={errorTitle}>{formError}</Alert>
          </div>
        )}
        {children}
      </form>
      </LabelsContext.Provider>
    </FormProvider>
  );
}

/** Set by a step after the save (e.g. submitting it) that already told the user the outcome. */
export const saveOutcomeTold = { current: false };

/** Field name → label, filled by each <Field>, so a refused submit can name the fields. */
const LabelsContext = createContext<Map<string, string> | null>(null);

function flattenErrors(errors: unknown, prefix = ''): [string, string][] {
  if (!errors || typeof errors !== 'object') return [];
  const e = errors as Record<string, unknown>;
  if (typeof e.message === 'string' && e.message && ('type' in e || 'ref' in e)) return [[prefix, e.message]];
  return Object.entries(e).filter(([k]) => k !== 'ref').flatMap(([k, v]) => flattenErrors(v, prefix ? `${prefix}.${k}` : k));
}

/** "lines.2.qty" → "Line 3 · Quantity" when Fields name it, else a readable version of the path. */
function labelFor(labels: Map<string, string>, path: string): string {
  const exact = labels.get(path) ?? labels.get(path.replace(/\.root$/, ''));
  if (exact) return exact;
  const parts = path.split('.').filter((p) => p !== 'root');
  const row = parts.findIndex((p) => /^\d+$/.test(p));
  const human = (s: string) => s.replace(/Id$/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
  if (row > 0) return `Line ${Number(parts[row]) + 1}${parts[row + 1] ? ` · ${labels.get(parts[row + 1]!) ?? human(parts[row + 1]!)}` : ''}`;
  return human(parts[parts.length - 1] ?? path);
}

function applyServerErrors<T extends FieldValues>(form: UseFormReturn<T, unknown, unknown>, e: unknown, setFormError: (m: string) => void) {
  if (e instanceof ApiError) {
    const fields = e.problem.errors ?? [];
    let placed = 0;
    for (const f of fields) {
      if (f.path && hasPath(form.getValues(), f.path)) {
        form.setError(f.path as Path<T>, { type: 'server', message: f.message }, { shouldFocus: placed === 0 });
        placed++;
      }
    }
    if (placed === 0 || placed < fields.length) setFormError(e.message);
    if (e.code === 'VERSION_CONFLICT') toast.error(e.message);
    return;
  }
  setFormError('Something went wrong. Please try again.');
}

/** True when `path` (dot-separated, e.g. "customer.taxCodeId" or "contacts.0.email") exists in the form values. */
function hasPath(values: unknown, path: string): boolean {
  let cur: unknown = values;
  for (const part of path.split('.')) {
    if (cur == null || typeof cur !== 'object' || !(part in (cur as object))) return false;
    cur = (cur as Record<string, unknown>)[part];
  }
  return true;
}

/** Enter moves to the next field instead of submitting (fast data entry); Ctrl+Enter submits. */
function enterToNext(e: KeyboardEvent<HTMLFormElement>, submitOnLast: boolean) {
  const t = e.target as HTMLElement;
  if (e.key !== 'Enter' || t.tagName === 'TEXTAREA' || t.tagName === 'BUTTON') return;
  e.preventDefault();
  if (e.ctrlKey || e.metaKey) {
    e.currentTarget.requestSubmit();
    return;
  }
  // Tick boxes and radios are skipped: Enter on them does nothing useful, so landing there only looks stuck.
  const focusables = Array.from(
    e.currentTarget.querySelectorAll<HTMLElement>('input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not(:disabled):not([readonly]), select:not(:disabled), textarea:not(:disabled), button[role=switch]:not(:disabled)'),
  );
  const at = focusables.indexOf(t);
  const next = at >= 0 ? focusables[at + 1] : undefined;
  if (next) next.focus();
  else if (submitOnLast) e.currentTarget.requestSubmit();
}

/** Label + control + inline error. Mandatory fields show a red asterisk. */
export function Field({
  name,
  label,
  required,
  hint,
  children,
  className,
  'data-tour': tour,
}: {
  name: string;
  label: string;
  required?: boolean;
  hint?: ReactNode;
  children: (props: { id: string; invalid: boolean; 'aria-describedby'?: string; 'aria-required'?: boolean }) => ReactNode;
  className?: string;
  /** Anchor for guided tours. */
  'data-tour'?: string;
}) {
  const id = useId();
  const labels = useContext(LabelsContext);
  useEffect(() => { labels?.set(name, label); }, [labels, name, label]);
  const { formState, getFieldState } = useFormContext();
  const error = getFieldState(name, formState).error;
  const message = error?.message ?? (error as unknown as { root?: { message?: string } })?.root?.message;
  const describedBy = message ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={cn('flex scroll-mt-24 flex-col gap-1.5', className)} data-tour={tour} data-invalid={message ? 'true' : undefined}>
      <label htmlFor={id} className="text-[13px] font-medium text-fg">
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        )}
      </label>
      {children({ id, invalid: !!message, 'aria-describedby': describedBy, 'aria-required': required || undefined })}
      {message ? (
        <p id={`${id}-err`} className="flex items-start gap-1 text-xs text-danger" role="alert">
          <AlertCircle className="mt-px size-3.5 shrink-0" />
          {message}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs text-fg-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Submit button: disabled while submitting, so a transaction can never be posted twice.
 * It does not take focus on mouse-down: otherwise the focused field's blur validation can
 * insert an error message above the button, shift it, and swallow the click.
 */
export function SubmitButton({ children, ...p }: ButtonProps) {
  const { formState } = useFormContext();
  // A short tick after a save that went through (the toast says what happened).
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!formState.isSubmitSuccessful) return;
    setDone(true);
    const t = setTimeout(() => setDone(false), 1400);
    return () => clearTimeout(t);
  }, [formState.isSubmitSuccessful, formState.submitCount]);
  return (
    <Button type="submit" loading={formState.isSubmitting} onMouseDown={(e) => e.preventDefault()} {...p}>
      {done && !formState.isSubmitting && (
        <svg viewBox="0 0 16 16" className="fx-check fx-pop" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M3.5 8.5l3 3 6-7" />
        </svg>
      )}
      {children}
    </Button>
  );
}

export function FormSection({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return (
    <section className="grid gap-x-8 gap-y-4 border-b border-border px-5 py-5 last:border-0 lg:grid-cols-[220px_1fr]">
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {description && <p className="mt-1 text-xs text-fg-muted">{description}</p>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}
